/**
 * Filters product gallery media by filename tokens: __variant-{slug}
 * Listens to Dawn PUB_SUB_EVENTS.variantChange; does not alter price/cart/buy logic.
 *
 * Targets:
 * - media-gallery viewer slides (.product__media-item[data-media-id])
 * - thumbnail list items ([data-target])
 * - product zoom modal content ([data-media-id])
 */
(function () {
  const HIDDEN_CLASS = 'fn-variant-media--hidden';
  const DATA_SCRIPT_PREFIX = 'fn-variant-media-data-';

  function normalizeToken(token) {
    if (!token) return null;
    return String(token)
      .toLowerCase()
      .replace(/_\d+x$/i, '')
      .replace(/^-+|-+$/g, '');
  }

  function extractVariantToken(filenameOrUrl) {
    if (!filenameOrUrl) return null;
    const name = String(filenameOrUrl).split('/').pop().split('?')[0];
    const match = name.match(/__variant-([a-z0-9-]+)/i);
    if (!match) return null;
    return normalizeToken(match[1]);
  }

  function slugify(value) {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function parseDataScript(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[fn-variant-media] Invalid JSON data', error);
      return null;
    }
  }

  function getMediaIdFromDomKey(domKey, sectionId) {
    if (!domKey) return null;
    const prefix = `${sectionId}-`;
    if (domKey.startsWith(prefix)) {
      return domKey.slice(prefix.length);
    }
    return domKey;
  }

  function findVariant(data, variantId) {
    if (!data?.variants?.length) return null;
    const id = variantId != null ? String(variantId) : null;
    if (id) {
      const byId = data.variants.find((variant) => String(variant.id) === id);
      if (byId) return byId;
    }
    return data.variants.find((variant) => String(variant.id) === String(data.selectedVariantId)) || data.variants[0];
  }

  function buildVariantSlugs(variant) {
    const slugs = new Set();
    (variant?.slugs || []).forEach((slug) => {
      const normalized = normalizeToken(slug) || slugify(slug);
      if (normalized) slugs.add(normalized);
    });
    if (variant?.title) {
      const titleSlug = slugify(variant.title);
      if (titleSlug) slugs.add(titleSlug);
    }
    (variant?.options || []).forEach((option) => {
      const optionSlug = slugify(option);
      if (optionSlug) slugs.add(optionSlug);
    });
    return slugs;
  }

  function buildMediaLookup(data) {
    const lookup = new Map();
    (data.media || []).forEach((item) => {
      const token =
        normalizeToken(item.variantToken) || extractVariantToken(item.filename) || extractVariantToken(item.url);
      lookup.set(String(item.id), {
        ...item,
        variantToken: token,
      });
    });
    return lookup;
  }

  function getGalleryRoot(sectionId) {
    return (
      document.getElementById(`MediaGallery-${sectionId}`) ||
      document.querySelector(`media-gallery[id^="MediaGallery-${sectionId}"]`) ||
      document.querySelector('media-gallery')
    );
  }

  function getModalRoot(sectionId) {
    return (
      document.getElementById(`ProductModal-${sectionId}`) ||
      document.querySelector(`product-modal[id^="ProductModal-${sectionId}"]`)
    );
  }

  function setItemVisibility(element, visible) {
    if (!element) return;
    element.classList.toggle(HIDDEN_CLASS, !visible);
    if (visible) {
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', 'true');
    }
  }

  function isMediaVisibleForVariant(mediaId, mediaLookup, slugs, featuredMediaId) {
    if (!mediaId) return true;
    const meta = mediaLookup.get(String(mediaId));
    const token = meta?.variantToken || null;
    if (!token) return true;
    return slugs.has(token) || (featuredMediaId && String(mediaId) === featuredMediaId);
  }

  function getViewerSlides(gallery) {
    return Array.from(
      gallery.querySelectorAll('.product__media-item[data-media-id], [id^="Slide-"][data-media-id]')
    ).filter((el) => !el.closest('.thumbnail-list') && !el.closest('.thumbnail-slider'));
  }

  function refreshViewerSlider(gallery) {
    const viewer =
      gallery.querySelector('[id^="GalleryViewer"]') ||
      gallery.querySelector('slider-component.slider-mobile-gutter:not(.thumbnail-slider)');
    if (viewer && typeof viewer.resetPages === 'function') {
      viewer.resetPages();
    }
  }

  function forceLoadImage(img, priority) {
    if (!img || img.tagName !== 'IMG') return;
    if (img.complete && img.naturalWidth > 0) return;

    img.loading = 'eager';
    if (priority && 'fetchPriority' in img) {
      img.fetchPriority = priority;
    }

    // Nudge browsers that deferred lazy images in display:none slides.
    if (img.dataset.fnPreloaded === 'true') return;
    img.dataset.fnPreloaded = 'true';

    const src = img.currentSrc || img.src;
    if (src) {
      const preloader = new Image();
      preloader.decoding = 'async';
      preloader.src = src;
    }

    if (typeof img.decode === 'function') {
      img.decode().catch(() => {});
    }
  }

  function getSlideMainImage(slide) {
    if (!slide) return null;
    return (
      slide.querySelector('.product__media img') ||
      slide.querySelector('img.image-magnify-lightbox, img.image-magnify-hover, img')
    );
  }

  function preloadViewerSlide(slide) {
    if (!slide) return;
    if (slide.hasAttribute('hidden') || slide.classList.contains(HIDDEN_CLASS)) return;
    const img = getSlideMainImage(slide);
    const priority = slide.classList.contains('is-active') ? 'high' : 'low';
    forceLoadImage(img, priority);
  }

  function preloadVisibleViewerImages(gallery) {
    if (!gallery) return;
    getViewerSlides(gallery).forEach((slide) => {
      if (slide.hasAttribute('hidden') || slide.classList.contains(HIDDEN_CLASS)) return;
      preloadViewerSlide(slide);
    });
  }

  function preloadSlideByTarget(gallery, targetId) {
    if (!gallery || !targetId) return;
    const slide =
      gallery.querySelector(`.product__media-item[data-media-id="${targetId}"]`) ||
      gallery.querySelector(`[id^="Slide-"][data-media-id="${targetId}"]`);
    preloadViewerSlide(slide);
  }

  function bindThumbnailPrefetch(gallery) {
    if (!gallery || gallery.dataset.fnThumbPrefetchBound === 'true') return;
    gallery.dataset.fnThumbPrefetchBound = 'true';

    const onPrefetch = (event) => {
      const thumb = event.target.closest?.('.thumbnail-list__item[data-target]');
      if (!thumb || !gallery.contains(thumb)) return;
      if (thumb.hasAttribute('hidden') || thumb.classList.contains(HIDDEN_CLASS)) return;
      preloadSlideByTarget(gallery, thumb.dataset.target);
    };

    gallery.addEventListener('pointerenter', onPrefetch, true);
    gallery.addEventListener('focusin', onPrefetch);
  }

  function filterModal(data, mediaLookup, slugs, featuredMediaId) {
    const modal = getModalRoot(data.sectionId);
    if (!modal) return;

    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return;

    Array.from(content.children).forEach((item) => {
      if (!data.hasVariantTokens) {
        setItemVisibility(item, true);
        return;
      }

      const idEl = item.matches('[data-media-id]') ? item : item.querySelector('[data-media-id]');
      const mediaId = getMediaIdFromDomKey(idEl?.dataset?.mediaId, data.sectionId);
      const visible = isMediaVisibleForVariant(mediaId, mediaLookup, slugs, featuredMediaId);
      setItemVisibility(item, visible);
    });
  }

  function filterGallery(data, variant) {
    const gallery = getGalleryRoot(data.sectionId);
    const mediaLookup = buildMediaLookup(data);
    const slugs = buildVariantSlugs(variant);
    const featuredMediaId = variant?.featuredMediaId != null ? String(variant.featuredMediaId) : null;
    const visibleMediaIds = new Set();

    if (gallery) {
      getViewerSlides(gallery).forEach((item) => {
        if (!data.hasVariantTokens) {
          setItemVisibility(item, true);
          const mediaId = getMediaIdFromDomKey(item.dataset.mediaId, data.sectionId);
          if (mediaId) visibleMediaIds.add(String(mediaId));
          return;
        }

        const mediaId = getMediaIdFromDomKey(item.dataset.mediaId, data.sectionId);
        const visible = isMediaVisibleForVariant(mediaId, mediaLookup, slugs, featuredMediaId);
        setItemVisibility(item, visible);
        if (visible && mediaId) visibleMediaIds.add(String(mediaId));
      });

      gallery.querySelectorAll('[data-target]').forEach((thumb) => {
        if (!data.hasVariantTokens) {
          setItemVisibility(thumb, true);
          return;
        }

        const mediaId = getMediaIdFromDomKey(thumb.dataset.target, data.sectionId);
        const shouldShow =
          !mediaId ||
          visibleMediaIds.has(String(mediaId)) ||
          isMediaVisibleForVariant(mediaId, mediaLookup, slugs, featuredMediaId);
        setItemVisibility(thumb, shouldShow);
      });

      if (data.hasVariantTokens) {
        ensureActiveMediaVisible(gallery, data.sectionId, featuredMediaId, visibleMediaIds);
      }
      refreshViewerSlider(gallery);
      preloadVisibleViewerImages(gallery);
      bindThumbnailPrefetch(gallery);
    }

    filterModal(data, mediaLookup, slugs, featuredMediaId);
  }

  function ensureActiveMediaVisible(gallery, sectionId, featuredMediaId, visibleMediaIds) {
    const slides = getViewerSlides(gallery).filter(
      (el) => !el.hasAttribute('hidden') && !el.classList.contains(HIDDEN_CLASS)
    );
    if (!slides.length) return;

    const active = gallery.querySelector(
      '.product__media-item[data-media-id].is-active, [id^="Slide-"][data-media-id].is-active'
    );
    const activeHidden =
      active && (active.hasAttribute('hidden') || active.classList.contains(HIDDEN_CLASS));

    if (active && !activeHidden) return;

    const preferredId =
      featuredMediaId && visibleMediaIds.has(String(featuredMediaId))
        ? `${sectionId}-${featuredMediaId}`
        : slides[0].dataset.mediaId;

    if (typeof gallery.setActiveMedia === 'function' && preferredId) {
      gallery.setActiveMedia(preferredId, true);
      return;
    }

    getViewerSlides(gallery).forEach((slide) => slide.classList.remove('is-active'));
    const next = gallery.querySelector(`[data-media-id="${preferredId}"]`) || slides[0];
    next?.classList.add('is-active');
  }

  function scheduleApply(apply, variantId) {
    // After Dawn updateMedia (gallery reorder + modal HTML replace).
    requestAnimationFrame(() => {
      apply(variantId);
      requestAnimationFrame(() => apply(variantId));
    });
  }

  function initFromScript(script) {
    const data = parseDataScript(script);
    if (!data || !data.sectionId) return null;

    const apply = (variantId) => {
      const variant = findVariant(data, variantId);
      filterGallery(data, variant);
    };

    apply(data.selectedVariantId);

    return { data, apply };
  }

  function boot() {
    const scripts = document.querySelectorAll(`script[type="application/json"][id^="${DATA_SCRIPT_PREFIX}"]`);
    if (!scripts.length) return;

    const controllers = [];
    scripts.forEach((script) => {
      const controller = initFromScript(script);
      if (controller) controllers.push(controller);
    });

    if (!controllers.length) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        const sectionId = event?.data?.sectionId;

        controllers.forEach(({ data, apply }) => {
          if (sectionId && String(data.sectionId) !== String(sectionId)) return;
          scheduleApply(apply, variantId);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
